import { LogOutModal } from "@/components/log-out-modal";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserFormFields } from "@/components/user-form";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import authService from "@/lib/auth";
import { AppRole, REGISTRATION_LABELS } from "@/lib/roles";
import { showToast } from "@/lib/toast";
import {
  CompleteOAuthFormData,
  completeOAuthSchema,
} from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { HelperText } from "react-native-paper";

export default function CompleteOAuthScreen() {
  const { user, setUser, theme, signOut } = useAuth();
  const { ref } = useLocalSearchParams<{ ref?: string }>();
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleCancelLogout = () => {
    if (loggingOut) {
      return;
    }

    setLogoutModalVisible(false);
  };

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      setLogoutModalVisible(false);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompleteOAuthFormData>({
    resolver: zodResolver(completeOAuthSchema),
    defaultValues: {
      first_name: user?.first_name,
      middle_name: user?.middle_name || "",
      last_name: user?.last_name,
      email: user?.email,
      phone: user?.phone,
      role: user?.role || "customer",
      address: "",
      referral_code: ref || "",
      origin_city: "",
      origin_latitude: undefined,
      origin_longitude: undefined,
    },
  });

  useEffect(() => {
    if (ref) {
      setValue("referral_code", ref);
    }
  }, [ref, setValue]);

  const role = watch("role");

  const onSubmit = async (data: CompleteOAuthFormData) => {
    try {
      const { user } = await authService.updateProfile({
        first_name: data.first_name,
        middle_name: data.middle_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        role: data.role,
        address: data.address,
        referral_code: data.referral_code || undefined,
        origin_city: data.origin_city,
        origin_latitude: data.origin_latitude,
        origin_longitude: data.origin_longitude,
      });
      setUser(user);
      router.replace("/(tabs)");
      showToast.success("Registration successful");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Registration failed";
      showToast.error(errorMessage);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={[styles.container, { backgroundColor: theme.background }]}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <View style={styles.header}>
            <ThemedText type="title">Complete Registration</ThemedText>
            <ThemedText>Complete your registration to get started</ThemedText>
          </View>

          <View style={styles.form}>
            <Label label="Role" />
            <View style={styles.roleSelector}>
              <Controller
                control={control}
                name="role"
                render={({ field: { onChange, value } }) => (
                  <>
                    {(
                      [
                        {
                          role: "customer" as AppRole,
                          label: REGISTRATION_LABELS.private_customer,
                        },
                        {
                          role: "business_customer" as AppRole,
                          label: REGISTRATION_LABELS.business_customer,
                        },
                        {
                          role: "driver" as AppRole,
                          label: REGISTRATION_LABELS.operator,
                        },
                      ] as const
                    ).map((option) => (
                      <TouchableOpacity
                        key={option.role}
                        style={[
                          styles.roleButton,
                          value === option.role && styles.roleButtonActive,
                        ]}
                        onPress={() => onChange(option.role)}
                      >
                        <ThemedText
                          style={[
                            styles.roleButtonText,
                            value === option.role &&
                              styles.roleButtonTextActive,
                          ]}
                          type="smallSemiBold"
                        >
                          {option.label}
                        </ThemedText>
                      </TouchableOpacity>
                    ))}
                  </>
                )}
              />
            </View>

            <UserFormFields
              control={control}
              errors={errors}
              setValue={setValue}
              role={role}
            />

            <View>
              <Label label="Referral Code" required={false} />
              <Controller
                control={control}
                name="referral_code"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    label="Referral Code (Optional)"
                    placeholder="Enter referral code"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    autoCapitalize="characters"
                    error={!!errors.referral_code}
                  />
                )}
              />
              {errors.referral_code && (
                <HelperText type="error" visible={!!errors.referral_code}>
                  {errors.referral_code?.message}
                </HelperText>
              )}
            </View>

            <BrandButton
              variant="primary"
              loading={isSubmitting}
              onPress={handleSubmit(onSubmit)}
            >
              {isSubmitting ? "Submitting..." : "Complete Registration"}
            </BrandButton>

            <BrandButton
              variant="error"
              onPress={() => setLogoutModalVisible(true)}
            >
              Logout
            </BrandButton>

            <LogOutModal
              visible={logoutModalVisible}
              onCancel={handleCancelLogout}
              onConfirm={handleConfirmLogout}
              loading={loggingOut}
            />
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.white,
  },
  scrollContent: {
    flexGrow: 1,
    width: "100%",
    maxWidth: 700,
    marginHorizontal: "auto",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    padding: 24,
    paddingTop: 60,
    paddingBottom: 40,
  },
  header: {
    gap: 10,
    marginBottom: 24,
  },
  form: {
    gap: 20,
  },
  roleSelector: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  roleButton: {
    flexGrow: 1,
    flexBasis: "30%",
    minWidth: "45%",
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    alignItems: "center",
  },
  roleButtonActive: {
    backgroundColor: BrandColors.primary,
    borderColor: BrandColors.primary,
  },
  roleButtonText: {
    fontSize: 14,
    fontWeight: "600",
  },
  roleButtonTextActive: {
    color: BrandColors.white,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
  },
  footerText: {
    fontSize: 14,
  },
  link: {
    fontSize: 14,
    color: BrandColors.primary,
    fontWeight: "600",
  },
});
